import TimePicker from 'rsuite/TimePicker';
import { parseTimeStringToDate } from '@/helpers/date/parseTimeStringToDate';
import { formatDateToTimeString } from '@/helpers/date/formatDateToTimeString';
import { Box } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { Palette } from '@/themes/themeOverrides/Palette';
import { useTranslate } from 'react-admin';

export const CustomTimePicker = ({ value, onChange, disabled, placeholder, error = false, ...props }) => {
    const translate = useTranslate();
    const defaultPlaceholder = placeholder || '--:--';
    const containerRef = useRef(null);
    const [resolvedPlacement, setResolvedPlacement] = useState('bottomStart');
    const getContainer = useCallback(() => containerRef.current ?? document.body, []);

    const handlePickerState = useCallback(isOpen => {
        const root = containerRef.current || document;
        const pickerElement = root.querySelector('.rs-picker .rs-input-group.rs-input-group-inside');
        if (pickerElement) {
            if (isOpen) {
                pickerElement.style.border = `2px solid ${Palette.gray.gray900}`;
                pickerElement.style.outline = `2px solid ${Palette.yellow.yellow200}`;
            } else {
                pickerElement.style.border = '';
                pickerElement.style.outline = '';
            }
        }
    }, []);
    const handleOpen = () => {
        const dialog = document.querySelector('.custom-dialog-right');
        if (dialog) {
            dialog.style.overflow = 'hidden';
        }

        const wrapperRect = containerRef.current?.getBoundingClientRect();
        if (wrapperRect) {
            const spaceAbove = wrapperRect.top;
            const spaceBelow = window.innerHeight - wrapperRect.bottom;
            const MIN_SPACE_FOR_TIME_PICKER_POPUP_PX = 300;
            setResolvedPlacement(spaceBelow < MIN_SPACE_FOR_TIME_PICKER_POPUP_PX && spaceAbove > spaceBelow ? 'topStart' : 'bottomStart');
        }
    };

    const handleClose = () => {
        const dialog = document.querySelector('.custom-dialog-right');
        if (dialog) {
            dialog.style.overflow = '';
        }
    };

    const handleChange = date => {
        const timeString = formatDateToTimeString(date);
        if (timeString && onChange) {
            onChange(timeString);
            handlePickerState(false);
        }
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                ...(error && {
                    '& .rs-picker .rs-input-group.rs-input-group-inside': {
                        border: `2px solid ${Palette.red.red500}`,
                    },
                }),
            }}
        >
            <TimePicker
                format='HH:mm'
                appearance='subtle'
                disabled={disabled}
                editable={false}
                placeholder={defaultPlaceholder}
                locale={{ ok: translate('common.timePicker.ok') }}
                placement={resolvedPlacement}
                value={parseTimeStringToDate(value)}
                onChange={handleChange}
                hideMinutes={minute => minute % 15 !== 0}
                container={getContainer}
                onOpen={() => {
                    handleOpen();
                    handlePickerState(true);
                }}
                onClose={() => {
                    handleClose();
                    handlePickerState(false);
                }}
                {...props}
            />
        </Box>
    );
};

export default CustomTimePicker;
